// ContentContext.jsx
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE_URL = 'https://royaleheritagefiesta.com/royaleheritagefiesta/api';
const USE_MOCK_DATA = true;

const ContentContext = createContext();

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within ContentProvider');
  }
  return context;
};

export const ContentProvider = ({ children }) => {
  const [sections, setSections] = useState({});
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0); // Add version to force re-renders

  // Load mock data for testing
  const loadMockData = () => {
    console.log("Loading mock data for testing...");
    
    const mockHomeContent = {
      hero: {
        title: "Royal Heritage Fiesta",
        description: "Where Legacy Meets Celebration — Experience timeless elegance and grandeur like never before."
      },
      services: {
        title: "Our Signature Experiences",
        subtitle: "Discover the essence of royal celebrations",
        title_1: "Royal Heritage Fiesta",
        desc_1: "Curated heritage experiences celebrating tradition and grandeur.",
        title_2: "Royal Dinners",
        desc_2: "Exquisite fine dining experiences with royal ambiance and culinary mastery.",
        title_3: "Music & Concerts",
        desc_3: "World-class performances and unforgettable musical experiences."
      },
      booking: {
        title: "Plan Your Royal Experience",
        description: "Share your details and we'll get back to you via WhatsApp",
        whatsapp_number: "919966252374",
        button_text: "Send via WhatsApp"
      },
      about: {
        title: "About Royal Heritage Fiesta",
        description: "Royal Heritage Fiesta is dedicated to creating unforgettable experiences that blend tradition with modern elegance. From majestic royal dinners to spectacular concerts, we bring your vision to life with impeccable attention to detail and timeless sophistication."
      },
      testimonials: {
        title: "What Our Guests Say",
        subtitle: "Experiences that speak for themselves",
        testimonial1_text: '"The Royal Heritage Fiesta was absolutely magnificent! Every detail was perfect."',
        testimonial1_name: "Vikram Singh",
        testimonial1_title: "Event Attendee",
        testimonial2_text: '"The Royal Dinners experience was beyond imagination. Truly world-class hospitality."',
        testimonial2_name: "Meera Kapoor",
        testimonial2_title: "Dinner Guest",
        testimonial3_text: '"The concert was spectacular! Perfect acoustics and unforgettable performances."',
        testimonial3_name: "Arjun Nair",
        testimonial3_title: "Music Lover"
      },
      location: {
        title: "Our Venues",
        subtitle: "Experience royalty at our premier locations",
        hq_name: "Hyderabad (HQ)",
        hq_address: "VIP Hills, Silicon Valley, Madhapur",
        hq_city: "Hyderabad - 500081, Telangana",
        hq_phone: "+91 86883 65247",
        hq_email: "royaleheritagefiesta@gmail.com"
      },
      cta: {
        title: "Ready To Experience Royalty?",
        subtitle: "Book your seat at our next grand celebration",
        phone1: "+919396536350",
        phone2: "+919966252374",
        button1_text: "Talk to us",
        button2_text: "Call +91 99662 52374"
      }
    };
    
    const mockNavbarContent = {
      brand_prefix: "ROYAL",
      brand_suffix: "HERITAGE",
      brand_name: "ROYAL HERITAGE FIESTA",
      events_text: "Events",
      check_events_text: "Check Events",
      book_events_text: "Book Events",
      dropdown_1: "Royal Heritage Fiesta",
      dropdown_2: "Royal Dinners",
      dropdown_3: "Music & Concerts",
    };
    
    const mockImages = {
      navbar_logo: "https://via.placeholder.com/200x60?text=Logo",
      carousel_1: "https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=1200",
      carousel_2: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1200",
      carousel_3: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1200",
      testimonial_1_avatar: "https://randomuser.me/api/portraits/men/32.jpg",
      testimonial_2_avatar: "https://randomuser.me/api/portraits/women/68.jpg",
      testimonial_3_avatar: "https://randomuser.me/api/portraits/men/45.jpg"
    };
    
    setSections({
      ...mockHomeContent,
      navbar: mockNavbarContent
    });
    setImages(mockImages);
  };

  const fetchContent = useCallback(async () => {
    try {
      setLoading(true);
      
      if (USE_MOCK_DATA) {
        console.log("Using mock data");
        loadMockData();
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_BASE_URL}/webpage/content`);
      
      if (response.data) {
        if (response.data.home) setSections(prev => ({ ...prev, ...response.data.home }));
        if (response.data.navbar) setSections(prev => ({ ...prev, navbar: response.data.navbar }));
        if (response.data.images) setImages(response.data.images);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      loadMockData();
    } finally {
      setLoading(false);
    }
  }, []);

  // Update section - THIS IS KEY - it must trigger a state update
  const updateSection = useCallback(async (sectionKey, sectionData) => {
    console.log(`Updating section: ${sectionKey}`, sectionData);
    
    // Update local state immediately - this triggers re-render
    setSections(prev => {
      const newSections = { 
        ...prev, 
        [sectionKey]: { ...prev[sectionKey], ...sectionData } 
      };
      console.log(`Sections updated:`, newSections);
      return newSections;
    });
    
    // Increment version to force re-render in components that depend on it
    setVersion(prev => prev + 1);
    
    if (USE_MOCK_DATA) {
      console.log("Demo mode: Section updated locally", sectionKey, sectionData);
      return true;
    }
    
    try {
      await axios.post(`${API_BASE_URL}/webpage/section/home/${sectionKey}`, sectionData);
      return true;
    } catch (error) {
      console.error('Error saving section:', error);
      return false;
    }
  }, []);

  const updateNavbar = useCallback(async (navbarData) => {
    console.log("Updating navbar", navbarData);
    
    setSections(prev => ({ 
      ...prev, 
      navbar: { ...prev.navbar, ...navbarData } 
    }));
    
    setVersion(prev => prev + 1);
    
    if (USE_MOCK_DATA) {
      console.log("Demo mode: Navbar updated locally", navbarData);
      return true;
    }
    
    try {
      await axios.post(`${API_BASE_URL}/webpage/navbar`, navbarData);
      return true;
    } catch (error) {
      console.error('Error saving navbar:', error);
      return false;
    }
  }, []);

  const updateImage = useCallback(async (imageKey, imageUrl) => {
    console.log(`Updating image: ${imageKey}`, imageUrl);
    
    setImages(prev => ({ 
      ...prev, 
      [imageKey]: imageUrl 
    }));
    
    setVersion(prev => prev + 1);
    
    if (USE_MOCK_DATA) {
      console.log("Demo mode: Image updated locally", imageKey, imageUrl);
      return true;
    }
    
    try {
      await axios.post(`${API_BASE_URL}/webpage/image/url`, {
        imageKey,
        imageUrl,
        pageName: 'home'
      });
      return true;
    } catch (error) {
      console.error('Error saving image:', error);
      return false;
    }
  }, []);

  const refreshContent = useCallback(() => {
    console.log("Refreshing content...");
    fetchContent();
  }, [fetchContent]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  // Helper function to get content by path
  const getContent = useCallback((path, defaultValue = '') => {
    const keys = path.split('.');
    let value = sections;
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return defaultValue;
      }
    }
    return value || defaultValue;
  }, [sections]);

  // Helper function to get image by key
  const getImage = useCallback((imageKey, defaultValue = null) => {
    return images[imageKey] || defaultValue;
  }, [images]);

  const value = {
    sections,
    images,
    loading,
    version, // Include version so components can depend on it
    getContent,
    getImage,
    updateSection,
    updateNavbar,
    updateImage,
    refreshContent,
    // Direct access for specific sections
    navbar: sections.navbar || {},
    hero: sections.hero || {},
    services: sections.services || {},
    about: sections.about || {},
    testimonials: sections.testimonials || {},
    location: sections.location || {},
    booking: sections.booking || {},
    cta: sections.cta || {},
  };

  return (
    <ContentContext.Provider value={value}>
      {children}
    </ContentContext.Provider>
  );
};